import {
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormSubmitHandler,
  isCurrency,
  openDialog,
  Tooltip,
} from 'platform/components';
import {Box, Hide, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {KeyboardEvent, ReactNode} from 'react';
import {useSelector} from 'react-redux';

import {equals, isNil, not} from 'ramda';
import {isNotNil, isUndefined} from 'ramda-adjunct';

import {selectTenant} from '@dms/api/core';
import {
  GetMinimumMarginCustomRecalculationApiResponse,
  GetServiceOrderCustomVatRateApiResponse,
  GetServiceOrderItemsDiscountApiResponse,
  GetSmallUnitMaterialCustomRecalculationApiResponse,
  useGetMinimumMarginCustomRecalculationQuery,
  useGetServiceOrderCustomerContractDetailsQuery,
  useGetServiceOrderCustomVatRateQuery,
  useGetServiceOrderItemsDiscountQuery,
  useGetServiceOrderQuery,
  useGetSmallUnitMaterialCustomRecalculationQuery,
  usePatchMinimumMarginCustomRecalculationMutation,
  usePatchServiceOrderCustomVatRateMutation,
  usePatchSmallUnitMaterialCustomRecalculationMutation,
  usePutServiceOrderItemsDiscountMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  DEFAULT_CURRENCY,
  getDecimalFromPercentage,
  getPercentageFromDecimal,
  handleApiError,
} from '@dms/shared';

import {ApiError, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {DiscountConfirmationDecision} from '../types/DiscountConfirmationDecision';
import {discountSectionSchema, DiscountSectionFormType} from '../types/discountSectionSchema';
import {MaterialDiscountOption} from '../types/materialDiscountOption';
import {WorkDiscountOption} from '../types/workDiscountOption';
import {CustomDiscountsCard} from './CustomDiscountsCard';
import {CustomVatRateCard} from './CustomVatRateCard';
import {DiscountConfirmationDialogContent} from './DiscountConfirmationDialogContent';
import {MinimumMarginCard} from './MinimumMarginCard';
import {PricingColumns} from './PricingColumns';
import {SmallUnitMaterialCard} from './SmallUnitMaterialCard';

interface DiscountSectionProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isReadOnly?: boolean;
  canEditDiscount: boolean;
  canEditCustomSmallUnitMaterial: boolean;
  /** Puts the two columns side by side instead of one under the other. */
  isSideBySide: boolean;
  /** Sections above Custom discounts in the left column. They save on their own, so they also show when the form fails to load. */
  context: ReactNode;
}

// The context sits inside the form element. Enter in one of its inputs, for example the vehicle
// class select with a closed menu, would submit the discount form, so the guard blocks it.
// Inputs in their own form, such as one in a dialog, keep their Enter.
const preventImplicitSubmit = (event: KeyboardEvent<HTMLDivElement>) => {
  const isDiscountFormInput =
    event.target instanceof HTMLInputElement &&
    event.target.form === event.currentTarget.closest('form');

  if (event.key === 'Enter' && isDiscountFormInput) {
    event.preventDefault();
  }
};

export function DiscountSection(props: DiscountSectionProps) {
  const {data, isLoading, isError} = useGetServiceOrderItemsDiscountQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });
  const {
    data: contract,
    isLoading: isContractLoading,
    isError: isContractError,
  } = useGetServiceOrderCustomerContractDetailsQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });
  const {
    data: smallUnitMaterialData,
    isLoading: isSmallUnitMaterialLoading,
    isError: isSmallUnitMaterialError,
  } = useGetSmallUnitMaterialCustomRecalculationQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });
  const {
    data: minimumMarginData,
    isLoading: isMinimumMarginLoading,
    isError: isMinimumMarginError,
  } = useGetMinimumMarginCustomRecalculationQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });
  const {
    data: customVatRateData,
    isLoading: isCustomVatRateLoading,
    isError: isCustomVatRateError,
  } = useGetServiceOrderCustomVatRateQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const isFormLoading =
    isLoading ||
    isContractLoading ||
    isSmallUnitMaterialLoading ||
    isMinimumMarginLoading ||
    isCustomVatRateLoading;
  // A failed refetch keeps the last data, so only a request that never loaded blocks the form.
  const isFormError =
    (isError && isUndefined(data)) ||
    (isContractError && isUndefined(contract)) ||
    (isSmallUnitMaterialError && isUndefined(smallUnitMaterialData));

  // Only a first load gates the form, and the context mounts together with it. A refetch keeps
  // the form and the context mounted, so neither loses its state. A request that never loaded
  // still shows the context, because it saves on its own.
  return match({isFormLoading, isFormError})
    .with({isFormLoading: true}, () => <DataStatus isLoading minHeight={45} />)
    .with({isFormError: true}, () => (
      <PricingColumns
        isSideBySide={props.isSideBySide}
        left={props.context}
        right={<DataStatus isError minHeight={45} />}
      />
    ))
    .otherwise(() => (
      <InnerForm
        data={data}
        smallUnitMaterialData={smallUnitMaterialData}
        minimumMarginData={minimumMarginData}
        isMinimumMarginError={isMinimumMarginError}
        customVatRateData={customVatRateData}
        isCustomVatRateError={isCustomVatRateError}
        hasCustomerContract={isNotNil(contract)}
        {...props}
      />
    ));
}

interface InnerFormProps extends DiscountSectionProps {
  data: GetServiceOrderItemsDiscountApiResponse | Nullish;
  smallUnitMaterialData: GetSmallUnitMaterialCustomRecalculationApiResponse | Nullish;
  minimumMarginData: GetMinimumMarginCustomRecalculationApiResponse | Nullish;
  isMinimumMarginError: boolean;
  customVatRateData: GetServiceOrderCustomVatRateApiResponse | Nullish;
  isCustomVatRateError: boolean;
  /** Work and material discounts come from the contract, so they are locked and never submitted. */
  hasCustomerContract: boolean;
}

// eslint-disable-next-line complexity -- Pre-existing violation
function InnerForm(props: InnerFormProps) {
  const {data: selectedTenant} = useSelector(selectTenant);

  const {data: serviceOrder} = useGetServiceOrderQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const currency = isCurrency(selectedTenant?.currency)
    ? selectedTenant?.currency
    : DEFAULT_CURRENCY;

  const [putServiceOrderItemsDiscount] = usePutServiceOrderItemsDiscountMutation();
  const [patchSmallUnitMaterialCustomRecalculation] =
    usePatchSmallUnitMaterialCustomRecalculationMutation();
  const [patchMinimumMarginCustomRecalculation] =
    usePatchMinimumMarginCustomRecalculationMutation();
  const [patchServiceOrderCustomVatRate] = usePatchServiceOrderCustomVatRateMutation();

  const canChangeCustomVatRate =
    !props.isCustomVatRateError && props.customVatRateData?.isChangeAllowed === true;

  const buildCustomVatRate = (data: DiscountSectionFormType) => ({
    isCustomVatEnabled: data.isCustomVatRateExpanded,
    vatType: data.isCustomVatRateExpanded ? data.customVatType : null,
  });

  const buildServiceOrderDiscount = (data: DiscountSectionFormType) => ({
    workDiscount: match([data.selectedWorkOption, data.isWorkExpanded])
      .with([WorkDiscountOption.PercentageDiscount, true], () => ({
        percentageDiscount: getDecimalFromPercentage(data.work?.percentDiscount?.discountRate),
      }))
      .with([WorkDiscountOption.DiscountedWorkRate, true], () => ({
        discountedWorkRate:
          data.work?.discountedWorkRates?.discountedWorkRate?.map((rate) => ({
            workType: rate.workRateType ?? '',
            workRateWithoutVat: isNil(rate?.workRatePriceWithoutVat)
              ? null
              : {
                  amount: rate.workRatePriceWithoutVat,
                  currency: String(currency),
                },
          })) ?? undefined,
      }))
      .otherwise(() => undefined),
    materialDiscount: match([data.selectedMaterialOption, data.isMaterialExpanded])
      .with([MaterialDiscountOption.DiscountOnSalePrice, true], () => ({
        discountToSalePrice: getDecimalFromPercentage(
          data.material?.percentDiscount?.discountOnSalePriceRate
        ),
      }))
      .with([MaterialDiscountOption.MarginToPurchasePrice, true], () => ({
        markupToPurchasePrice: getDecimalFromPercentage(
          data.material?.percentDiscount?.discountMarginToPurchasePriceRate
        ),
      }))
      .otherwise(() => undefined),
  });

  const requestDiscountConfirmation = () =>
    new Promise<DiscountConfirmationDecision>((resolve) => {
      openDialog(
        <DiscountConfirmationDialogContent
          onDecision={resolve}
          data-testid={suffixTestId('confirmationDialog', props)}
        />,
        {
          id: 'serviceOrderDiscountConfirmationDialog',
          title: i18n.t('entity.order.discountConfirmation.title'),
          size: 'small',
          onCloseComplete: () => resolve('discard'),
        }
      );
    });

  const handleSubmit: FormSubmitHandler<DiscountSectionFormType> = async (
    data,
    _setErrors,
    reset,
    blockerContext
  ) => {
    let hasApiError = false;

    const onApiError = (error: ApiError) => {
      hasApiError = true;
      handleApiError(error, {signalApiError: blockerContext?.reportApiFailure});
    };

    // Clears the dirty state so the unsaved-changes blocker stops firing once everything is saved.
    // A failed request leaves the form dirty so the change stays guarded.
    const markAsSaved = () => {
      if (!hasApiError) {
        // Passing the current values makes them the new dirty baseline. Resetting with `undefined`
        // would keep comparing against the values the form mounted with, so editing a field and
        // then reverting it after a save would read as pristine and slip past the blocker.
        reset((values) => values, {keepValues: true});
      }
    };

    const serviceOrderDiscount = buildServiceOrderDiscount(data);

    const putDiscount = (isDiscountApply: boolean) =>
      putServiceOrderItemsDiscount({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
        body: {...serviceOrderDiscount, isDiscountApply},
      })
        .unwrap()
        .catch(onApiError);

    const fixedValueAmount = data.isSmallUnitMaterialExpanded
      ? (data.smallUnitMaterial?.fixedValueAmountWithVat ??
        data.smallUnitMaterial?.fixedValueAmountWithoutVat)
      : undefined;

    // Not redundant with the card's read-only state: the whole form submits together, so without
    // this guard the PATCH would re-send small unit material the user is not allowed to own.
    if (props.canEditCustomSmallUnitMaterial) {
      await patchSmallUnitMaterialCustomRecalculation({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
        body: {
          isFixedValueEnabled: data.isSmallUnitMaterialExpanded,
          isFixedValueWithVat: data.isSmallUnitMaterialExpanded ? data.isFixedValueWithVat : false,
          fixedValueAmount,
        },
      })
        .unwrap()
        .catch(onApiError);
    }

    if (props.canEditDiscount && !props.isMinimumMarginError) {
      await patchMinimumMarginCustomRecalculation({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
        body: {
          isCustomEnabled: data.isMinimumMarginExpanded,
          customMaterialMargin: data.isMinimumMarginExpanded
            ? (getDecimalFromPercentage(data.minimumMargin?.material) ?? null)
            : null,
          customLabourMargin: data.isMinimumMarginExpanded
            ? (getDecimalFromPercentage(data.minimumMargin?.labour) ?? null)
            : null,
        },
      })
        .unwrap()
        .catch(onApiError);
    }

    const customVatRate = buildCustomVatRate(data);
    const hasCustomVatRateChanged = not(equals(customVatRate, buildCustomVatRate(defaultValues)));

    // Sent only on a change: the PATCH recalculates the VAT of every item in the service order.
    if (canChangeCustomVatRate && hasCustomVatRateChanged) {
      await patchServiceOrderCustomVatRate({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
        body: customVatRate,
      })
        .unwrap()
        .catch(onApiError);
    }

    // Everything below writes the discount itself. Returning here keeps a user who may only edit
    // small unit material from firing the discount PUT or seeing the apply-discount dialog, while
    // still clearing the dirty state so the unsaved-changes blocker stops firing.
    if (props.hasCustomerContract || !props.canEditDiscount) {
      markAsSaved();
      return;
    }

    const originalDiscount = buildServiceOrderDiscount(defaultValues);
    const hasDiscountChanged = not(equals(serviceOrderDiscount, originalDiscount));

    if (!serviceOrder?.hasItems || !hasDiscountChanged) {
      await putDiscount(true);
      markAsSaved();
      return;
    }

    const isDiscountApply = match(await requestDiscountConfirmation())
      .with('apply', () => true)
      .with('doNotApply', () => false)
      .with('discard', () => null)
      .exhaustive();

    if (isNil(isDiscountApply)) {
      // The blocker navigates away as soon as this handler resolves - reporting a failed submit
      // keeps the unsaved change on screen instead of silently discarding it.
      blockerContext?.reportApiFailure?.();
      return;
    }

    await putDiscount(isDiscountApply);
    markAsSaved();
  };

  const canSaveChanges =
    props.canEditDiscount || props.canEditCustomSmallUnitMaterial || canChangeCustomVatRate;

  const defaultValues: DiscountSectionFormType = {
    isWorkExpanded: isNotNil(props.data?.workDiscount),
    selectedWorkOption: isNil(props.data?.workDiscount?.discountedWorkRate)
      ? WorkDiscountOption.PercentageDiscount
      : WorkDiscountOption.DiscountedWorkRate,
    isMaterialExpanded: isNotNil(props.data?.materialDiscount),
    selectedMaterialOption: isNil(props.data?.materialDiscount?.markupToPurchasePrice)
      ? MaterialDiscountOption.DiscountOnSalePrice
      : MaterialDiscountOption.MarginToPurchasePrice,
    isSmallUnitMaterialExpanded: props.smallUnitMaterialData?.isFixedValueEnabled ?? false,
    isFixedValueWithVat: props.smallUnitMaterialData?.isFixedValueWithVat ?? false,
    isMinimumMarginExpanded: props.minimumMarginData?.isCustomEnabled ?? false,
    isCustomVatRateExpanded: props.customVatRateData?.isCustomVatEnabled ?? false,
    customVatType: props.customVatRateData?.vatType ?? null,
    work: {
      percentDiscount: {
        discountRate:
          getPercentageFromDecimal(props.data?.workDiscount?.percentageDiscount) ?? undefined,
      },
      discountedWorkRates: {
        discountedWorkRate: props.data?.workDiscount?.discountedWorkRate?.map((workRate) => ({
          workRateType: workRate?.workType ?? undefined,
          workRatePriceWithoutVat: workRate?.workRateWithoutVat?.amount ?? undefined,
        })) ?? [{workRateType: undefined, workRatePriceWithoutVat: undefined}],
      },
    },
    material: {
      percentDiscount: {
        discountOnSalePriceRate:
          getPercentageFromDecimal(props.data?.materialDiscount?.discountToSalePrice) ?? undefined,
        discountMarginToPurchasePriceRate:
          getPercentageFromDecimal(props.data?.materialDiscount?.markupToPurchasePrice) ??
          undefined,
      },
    },
    smallUnitMaterial: {
      fixedValueAmountWithoutVat:
        isNotNil(props.smallUnitMaterialData?.isFixedValueWithVat) &&
        isNotNil(props.smallUnitMaterialData?.fixedValueAmount) &&
        !props.smallUnitMaterialData?.isFixedValueWithVat
          ? props.smallUnitMaterialData?.fixedValueAmount
          : undefined,
      fixedValueAmountWithVat:
        isNotNil(props.smallUnitMaterialData?.isFixedValueWithVat) &&
        isNotNil(props.smallUnitMaterialData?.fixedValueAmount) &&
        props.smallUnitMaterialData?.isFixedValueWithVat
          ? props.smallUnitMaterialData?.fixedValueAmount
          : undefined,
    },
    minimumMargin: {
      material: getPercentageFromDecimal(props.minimumMarginData?.customMaterialMargin) ?? null,
      labour: getPercentageFromDecimal(props.minimumMarginData?.customLabourMargin) ?? null,
    },
  };

  // The form stays mounted across refetches, so `values` takes in new server data, for example
  // after an order variant change. Fields with unsaved edits keep them.
  return (
    <Form<DiscountSectionFormType>
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      values={defaultValues}
      resetOptions={{keepDirtyValues: true}}
      experimentalZodSchema={discountSectionSchema}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <PricingColumns
            isSideBySide={props.isSideBySide}
            left={
              <>
                <Box onKeyDownCapture={preventImplicitSubmit}>
                  <VStack spacing={4}>{props.context}</VStack>
                </Box>
                <CustomDiscountsCard
                  control={control}
                  formApi={formApi}
                  currency={currency}
                  isReadOnly={
                    props.isReadOnly || props.hasCustomerContract || !props.canEditDiscount
                  }
                  data-testid={props['data-testid']}
                />
              </>
            }
            right={
              <>
                <DataStatus isError={props.isCustomVatRateError} minHeight={20}>
                  <CustomVatRateCard
                    control={control}
                    formApi={formApi}
                    isReadOnly={props.isReadOnly || !canChangeCustomVatRate}
                    vatSource={props.customVatRateData?.vatSource}
                    data-testid={suffixTestId('customVatRate', props)}
                  />
                </DataStatus>
                <SmallUnitMaterialCard
                  control={control}
                  formApi={formApi}
                  currency={currency}
                  whatIfAmountWithoutVat={props.smallUnitMaterialData?.whatIfAmountWithoutVat}
                  whatIfAmountWithVat={props.smallUnitMaterialData?.whatIfAmountWithVat}
                  isReadOnly={props.isReadOnly || !props.canEditCustomSmallUnitMaterial}
                  data-testid={suffixTestId('smallUnitMaterial', props)}
                />
                <DataStatus isError={props.isMinimumMarginError} minHeight={20}>
                  <MinimumMarginCard
                    control={control}
                    formApi={formApi}
                    serviceCaseId={props.serviceCaseId}
                    serviceOrderId={props.serviceOrderId}
                    minimumMarginData={props.minimumMarginData}
                    isReadOnly={props.isReadOnly || !props.canEditDiscount}
                    data-testid={suffixTestId('minimumMargin', props)}
                  />
                </DataStatus>
              </>
            }
          />
          <Hide when={props.isReadOnly}>
            <ButtonGroup align="right">
              <Tooltip isDisabled={canSaveChanges} label={i18n.t('general.labels.noPermission')}>
                <FormButton
                  control={control}
                  type="submit"
                  isDisabled={!canSaveChanges}
                  title={i18n.t('general.actions.saveChanges')}
                  data-testid={suffixTestId('save', props)}
                />
              </Tooltip>
            </ButtonGroup>
          </Hide>
        </VStack>
      )}
    </Form>
  );
}
